import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { useTheme } from '@material-ui/core/styles';
import { Box, Card, Stack, Typography } from '@material-ui/core';
import ReactApexChart from 'react-apexcharts';
import BaseOptionChart from '../charts/BaseOptionChart';
import { computePeakConcurrentByDay } from '../../utils/billing';

// Peak-usage bar chart that sits between `DeploymentsKpiRow` and
// `DeploymentsTableCard` on both the team-scoped and company-wide
// deployment views. One bar per DAY of the selected month, height =
// summed `quantity` of deployments that were *concurrently running on
// that day*. The chart and the table below it share the same
// definition of "active on day D", so clicking a bar highlights in
// the table exactly the racks that contributed to that bar's height --
// and conversely the table's highlighted-row quantity sum equals the
// bar height for the clicked day.
//
// "Concurrent" uses the deployment-level `quantity` field (NOT
// `billable_quantity` -- the latter is a billing-side adjustment that
// can drop to 0 on a replaced rack even while the rack is still
// deployed). Each deployment contributes its `quantity` for the
// half-open interval `[provision_date, decommission_date)`; a
// deployment is active on day D iff that interval overlaps the day
// window `[D 00:00 UTC, D+1 00:00 UTC)`. The actual computation
// lives in `computePeakConcurrentByDay` (shared with `DeploymentsKpiRow`
// so the bar chart and the "Max Peak Usage" KPI stay in
// sync).
//
// Three visual states per bar, all in the primary color family so
// the chart reads as a single visual story:
//   1. Active day (`activeDay` matches)   -> palette.primary.dark
//      Darker shade so the selected bar visually "sinks in" -- the
//      user knows they've drilled into this day.
//   2. Future day (after today)            -> palette.primary.light
//      Lighter shade so projected/zero days read as less prominent
//      than historical days without introducing a brand-new hue.
//   3. Otherwise (past/present days)       -> palette.primary.main
//      The default. Stays consistent with the page's primary brand
//      color and adapts to light/dark mode automatically.
//
// The monthly peak is shown as a horizontal DASHED LINE that spans
// the chart at `monthlyPeak` units (rendered via ApexCharts
// `annotations.yaxis`). The line is labeled with the peak value so
// the user can read the peak at a glance -- and because the line
// spans every bar, every bar that's at or above the line is visually
// a "peak day" without needing per-bar red coloring. This keeps the
// bar palette neutral and stops the chart from hiding which days
// tied for the peak (multiple bars can sit on the dashed line at
// once without competing for color emphasis).
//
// Clicking a bar calls `onActiveDayChange(day)`. Clicking the already-
// active bar toggles the filter off (passes `null`) so the user has a
// quick way back to the unfiltered view without a separate "Clear"
// button on the chart.
//
// When `month` is null (MonthPicker hasn't seeded yet) or no day has
// a positive total, the empty-state copy renders instead of the chart.
// Future days of the *current* month are NOT hidden -- they appear as
// zero-height placeholder bars in grey so the user always sees a full
// month rather than a truncated chart that drops off mid-month.

DeploymentsPeakUsageCard.propTypes = {
  deployments: PropTypes.array,
  month: PropTypes.instanceOf(Date),
  activeDay: PropTypes.string,
  onActiveDayChange: PropTypes.func,
  description: PropTypes.string,
  emptyMessage: PropTypes.string,
  noCard: PropTypes.bool
};

DeploymentsPeakUsageCard.defaultProps = {
  deployments: [],
  month: null,
  activeDay: null,
  onActiveDayChange: () => {},
  description: '',
  emptyMessage: 'No deployments to chart.',
  noCard: false
};

// Sum concurrent quantity per day using the shared util so the chart
// and the "Max Peak Usage" KPI read from the same source.
// See `computePeakConcurrentByDay` in `src/utils/billing.js` for the
// full algorithm; the short version is: each deployment contributes
// its `quantity` to every day whose UTC window overlaps the
// deployment's `[provision_date, decommission_date)` half-open
// interval.
//
// Returns `null` when `month` hasn't been seeded yet (the MonthPicker
// hasn't picked anything) so the caller can render the empty state
// instead of a chart with no x-axis. Once a month is picked, the
// returned `days` cover every day of the month, including future
// days of the current month -- those are zero-height placeholder bars
// (the util doesn't know the future yet) so the user always sees a
// full month rather than a truncated chart.
function aggregateByDay(deployments, month) {
  return computePeakConcurrentByDay(deployments, month);
}

export default function DeploymentsPeakUsageCard({
  deployments,
  month,
  activeDay,
  onActiveDayChange,
  description,
  emptyMessage,
  noCard
}) {
  const theme = useTheme();
  const baseOptions = BaseOptionChart();

  const aggregated = useMemo(() => aggregateByDay(deployments, month), [deployments, month]);
  // The chart renders whenever at least one day has positive activity.
  // Future days of the current month contribute 0 and read as
  // zero-height placeholder bars in grey -- the full month stays
  // visible rather than truncating at "today".
  const hasData = aggregated && aggregated.totals.some((total) => total > 0);

  // Today's ISO day drives the "future day" color bucket. The day
  // buckets in `aggregated.days` are UTC calendar days (see
  // `computePeakConcurrentByDay`), so `today` must also be a UTC day --
  // otherwise a viewer outside UTC could see "today"'s bar colored as
  // future (or the previous UTC day still treated as not-yet-past)
  // right around local midnight.
  const today = useMemo(() => {
    const now = new Date();
    const year = now.getUTCFullYear();
    const month = String(now.getUTCMonth() + 1).padStart(2, '0');
    const day = String(now.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);

  const chartOptions = useMemo(() => {
    if (!hasData) return null;
    // modify colors for the chart
    const colors = aggregated.days.map((iso) => {
      // Active day color
      if (iso === activeDay) {
        return theme.palette.mode === 'dark'
          ? theme.palette.primary.main
          : theme.palette.primary.lighter;
      }
      if (iso > today) return theme.palette.primary.darker;
      return theme.palette.primary.light;
    });
    // Per-data-point colors for the data labels themselves
    const dataLabelColors = aggregated.days.map((iso) => {
      if (iso === activeDay) return theme.palette.primary.dark;
      if (iso > today) return theme.palette.primary.light;
      return theme.palette.primary.main;
    });
    return {
      ...baseOptions,
      // Hide the legend
      legend: {
        ...baseOptions.legend,
        show: false
      },
      chart: {
        ...baseOptions.chart,
        // click handler when a day bar has been clicked
        events: {
          dataPointSelection: (_event, _ctx, config) => {
            const idx = config?.dataPointIndex;
            if (idx == null) return;
            if (aggregated.isFutureDays?.[idx]) return;
            const iso = aggregated.days[idx];
            if (!iso) return;
            onActiveDayChange(iso === activeDay ? null : iso);
          }
        }
      },
      // Dashed horizontal line at `monthlyPeak` units. ApexCharts
      // renders `annotations.yaxis` as a stroke + label anchored to
      // the chart. The label sits at the chart's right edge by
      // default, which keeps the bar labels on top of the bars from
      // overlapping the line label.
      annotations: {
        yaxis: [
          {
            y: aggregated.monthlyPeak,
            borderColor: theme.palette.error.main,
            strokeDashArray: 6,
            borderWidth: 2,
            // "Peak (8)" reads as both the metric and its value --
            // the line by itself wouldn't tell the viewer what the
            // height means.
            label: {
              text: `Peak (${aggregated.monthlyPeak})`,
              textAnchor: 'start',
              position: 'left',
              offsetX: 8,
              style: {
                ...theme.typography.subtitle2,
                color: theme.palette.error.main,
                background: 'transparent'
              }
            }
          }
        ]
      },
      colors,
      dataLabelColors,
      plotOptions: {
        ...baseOptions.plotOptions,
        bar: {
          ...baseOptions.plotOptions.bar,
          // Tighten column width so the bars read as discrete.
          columnWidth: '55%',
          distributed: true
        }
      },
      xaxis: {
        ...baseOptions.xaxis,
        // Strip YYYY-MM to display only the day-of-month ("1", "2",
        // ... "31"). The full date is still available in the tooltip.
        categories: aggregated.days.map((iso) => iso.slice(8)),
        labels: {
          ...theme.typography.body2,
          colors: theme.palette.text.secondary,
          rotate: 0,
          style: {
            colors: theme.palette.text.secondary,
            fontSize: '12px'
          }
        },
        title: {
          text: 'Day of month',
          style: {
            ...theme.typography.subtitle2,
            color: theme.palette.text.secondary
          }
        },
        tooltip: { enabled: false }
      },
      yaxis: {
        labels: {
          ...theme.typography.body2,
          style: { colors: theme.palette.text.secondary }
        },
        title: {
          text: 'Concurrent units',
          style: {
            ...theme.typography.subtitle2,
            color: theme.palette.text.secondary
          }
        }
      },
      tooltip: {
        ...baseOptions.tooltip,
        x: {
          // Rebuild the full date from the displayed day-of-month so
          // the tooltip reads e.g. "Day 15 (2026-02-15)".
          formatter: (val) => {
            const iso = aggregated.days[Number(val) - 1];
            return iso ? `Day ${val} (${iso})` : `Day ${val}`;
          }
        },
        y: {
          title: { formatter: () => 'Concurrent units' },
          formatter: (val) => `${val}`
        }
      },
      dataLabels: {
        enabled: true,
        // Label only the active day. The peak value is communicated
        // via the dashed annotation line, so we don't need per-bar
        // labels for peak days (and a 31-bar month full of "8"
        // labels stacked on the dashed line would clutter the
        // chart).
        formatter: (val, opts) => {
          const iso = aggregated.days[opts.dataPointIndex];
          if (iso === activeDay) return val;
          return '';
        },
        offsetY: -20,
        // ApexCharts calls `.slice()` on `dataLabels.style.colors`, so it
        // must be an array of color strings (one per data point). The
        // parallel `dataLabelColors` array (computed above alongside the
        // bar `colors`) gives us per-bar label coloring without tripping
        // the function-call expectation.
        style: {
          ...theme.typography.subtitle2,
          colors: dataLabelColors
        }
      }
    };
  }, [baseOptions, aggregated, today, hasData, theme, activeDay, onActiveDayChange]);

  const chartSeries = useMemo(
    () => (hasData ? [{ name: 'Concurrent units', data: aggregated.totals }] : []),
    [aggregated, hasData]
  );

  // `noCard` lets the combined chart+table view drop the outer Card
  // so the wrapper's own Card doesn't nest inside another Card.
  const body = (
    <Box data-testid="peak-usage-card">
      <Box sx={{ px: 3, pt: 2 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={1}
        >
          <Typography variant="h6">Peak Usage</Typography>
        </Stack>
        {description && (
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        )}
      </Box>
      <Box sx={{ p: 3, pt: 1 }}>
        {hasData ? (
          <Box sx={{ width: '100%', minHeight: 320 }}>
            <ReactApexChart type="bar" options={chartOptions} series={chartSeries} height={320} />
          </Box>
        ) : (
          <Box sx={{ py: 6, textAlign: 'center' }} data-testid="peak-usage-empty">
            <Typography variant="body2" color="text.secondary">
              {emptyMessage}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
  if (noCard) return body;
  return <Card>{body}</Card>;
}
